import { useState, useEffect } from 'react';
import { CAPAType, CAPA } from '@cbm/shared';
import { 
  Plus, 
  Calendar, 
  User, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Edit,
  Save,
  X,
  FileText,
  Target,
  Shield,
  Wrench
} from 'lucide-react';

interface CAPAWorkflowProps {
  ncrId: string;
  existingCAPAs?: CAPA[];
  onCAPACreate: (capa: CAPACreateRequest) => void;
  onCAPAUpdate: (capaId: string, updates: CAPAUpdateRequest) => void;
  onKnowledgeBaseCreate: (capaId: string, entry: KnowledgeBaseEntry) => void;
  loading?: boolean;
}

interface CAPACreateRequest {
  ncrId: string;
  type: CAPAType;
  description: string;
  assignedTo: string;
  dueDate: string;
}

interface CAPAUpdateRequest {
  status?: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED';
  completedAt?: string;
}

interface KnowledgeBaseEntry {
  title: string;
  content: string;
  tags: string[];
  category: string;
}

export function CAPAWorkflow({ 
  ncrId, 
  existingCAPAs = [], 
  onCAPACreate, 
  onCAPAUpdate, 
  onKnowledgeBaseCreate,
  loading = false 
}: CAPAWorkflowProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCapa, setEditingCapa] = useState<string | null>(null);
  const [showKnowledgeForm, setShowKnowledgeForm] = useState<string | null>(null);
  
  // Create form state
  const [createForm, setCreateForm] = useState<CAPACreateRequest>({
    ncrId,
    type: 'corrective',
    description: '',
    assignedTo: '',
    dueDate: ''
  });

  // Knowledge base form state
  const [knowledgeForm, setKnowledgeForm] = useState<KnowledgeBaseEntry>({
    title: '',
    content: '',
    tags: [],
    category: 'general'
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    setCreateForm(prev => ({ ...prev, ncrId }));
  }, [ncrId]);

  const handleCreateSubmit = () => {
    if (!createForm.description.trim() || !createForm.assignedTo.trim() || !createForm.dueDate) {
      return;
    }
    
    onCAPACreate(createForm);
    setCreateForm({
      ncrId,
      type: 'corrective',
      description: '',
      assignedTo: '',
      dueDate: ''
    });
    setShowCreateForm(false);
  };

  const handleStatusUpdate = (capaId: string, status: CAPAUpdateRequest['status']) => {
    const updates: CAPAUpdateRequest = { status };
    if (status === 'COMPLETED') {
      updates.completedAt = new Date().toISOString();
    }
    onCAPAUpdate(capaId, updates);
    setEditingCapa(null);
  };

  const handleKnowledgeSubmit = (capaId: string) => {
    if (!knowledgeForm.title.trim() || !knowledgeForm.content.trim()) {
      return;
    }
    
    onKnowledgeBaseCreate(capaId, knowledgeForm);
    setKnowledgeForm({
      title: '',
      content: '',
      tags: [],
      category: 'general'
    });
    setShowKnowledgeForm(null);
  };

  const addTag = () => {
    if (tagInput.trim() && !knowledgeForm.tags.includes(tagInput.trim())) {
      setKnowledgeForm(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setKnowledgeForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'text-red-600 bg-red-100';
      case 'IN_PROGRESS': return 'text-blue-600 bg-blue-100';
      case 'COMPLETED': return 'text-green-600 bg-green-100';
      case 'CLOSED': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type: CAPAType) => {
    switch (type) {
      case 'corrective': return <Wrench className="w-4 h-4" />;
      case 'preventive': return <Shield className="w-4 h-4" />;
      case 'design': return <Target className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const isOverdue = (dueDate: string, status: string) => {
    return status !== 'COMPLETED' && status !== 'CLOSED' && new Date(dueDate) < new Date();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Corrective & Preventive Actions
          </h3>
          <p className="text-sm text-gray-600">NCR: {ncrId}</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          disabled={loading}
        >
          <Plus className="w-4 h-4" />
          <span>Create CAPA</span>
        </button>
      </div>

      {/* Create CAPA Form */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium">Create New CAPA</h4>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action Type
              </label>
              <div className="flex space-x-4">
                {(['corrective', 'preventive', 'design'] as CAPAType[]).map((type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="radio"
                      name="capaType"
                      value={type}
                      checked={createForm.type === type}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, type: e.target.value as CAPAType }))}
                      className="mr-2"
                    />
                    <div className="flex items-center space-x-1">
                      {getTypeIcon(type)}
                      <span className="text-sm capitalize">{type}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={createForm.description}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the corrective or preventive action..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To
                </label>
                <input
                  type="email"
                  value={createForm.assignedTo}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, assignedTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={createForm.dueDate.split('T')[0]}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, dueDate: e.target.value + 'T23:59:59.999Z' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSubmit}
                disabled={!createForm.description.trim() || !createForm.assignedTo.trim() || !createForm.dueDate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Create CAPA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CAPA List */}
      <div className="space-y-4">
        {existingCAPAs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No CAPAs created yet</p>
            <p className="text-sm">Create a CAPA to track corrective and preventive actions</p>
          </div>
        ) : (
          existingCAPAs.map((capa) => (
            <div key={capa.capaId} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {getTypeIcon(capa.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(capa.status)}`}>
                        {capa.status.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500 uppercase font-medium">
                        {capa.type}
                      </span>
                      {isOverdue(capa.dueDate, capa.status) && (
                        <div className="flex items-center space-x-1 text-red-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs font-medium">OVERDUE</span>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-900 mb-3">{capa.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{capa.assignedTo}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span className={isOverdue(capa.dueDate, capa.status) ? 'text-red-600' : ''}>
                          Due: {formatDate(capa.dueDate)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>Created: {formatDate(capa.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {capa.status !== 'CLOSED' && (
                    <button
                      onClick={() => setEditingCapa(editingCapa === capa.capaId ? null : capa.capaId)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {capa.status === 'COMPLETED' && (
                    <button
                      onClick={() => setShowKnowledgeForm(capa.capaId)}
                      className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                    >
                      Add to Knowledge Base
                    </button>
                  )}
                </div>
              </div>

              {/* Status Update Controls */}
              {editingCapa === capa.capaId && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700">Update Status:</span>
                    {(['OPEN', 'IN_PROGRESS', 'COMPLETED', 'CLOSED'] as const).map((status) => (
                      <button
                        key={status}
                        onClick={() => handleStatusUpdate(capa.capaId, status)}
                        disabled={capa.status === status}
                        className={`
                          px-3 py-1 text-xs rounded transition-colors
                          ${capa.status === status 
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}
                        `}
                      >
                        {status.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Knowledge Base Form */}
              {showKnowledgeForm === capa.capaId && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h5 className="text-lg font-medium mb-4">Create Knowledge Base Entry</h5>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={knowledgeForm.title}
                        onChange={(e) => setKnowledgeForm(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Lesson learned title..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content
                      </label>
                      <textarea
                        value={knowledgeForm.content}
                        onChange={(e) => setKnowledgeForm(prev => ({ ...prev, content: e.target.value }))}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Describe the lesson learned, best practices, or preventive measures..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category
                        </label>
                        <select
                          value={knowledgeForm.category}
                          onChange={(e) => setKnowledgeForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="general">General</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="operations">Operations</option>
                          <option value="quality">Quality</option>
                          <option value="safety">Safety</option>
                          <option value="design">Design</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tags
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addTag()}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Add tag..."
                          />
                          <button
                            onClick={addTag}
                            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                          >
                            Add
                          </button>
                        </div>
                        {knowledgeForm.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {knowledgeForm.tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                              >
                                {tag}
                                <button
                                  onClick={() => removeTag(tag)}
                                  className="ml-1 text-blue-500 hover:text-blue-700"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setShowKnowledgeForm(null)}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleKnowledgeSubmit(capa.capaId)}
                        disabled={!knowledgeForm.title.trim() || !knowledgeForm.content.trim()}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save to Knowledge Base</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}